import * as React from 'react';
import { StyleSheet, View } from 'react-native';

import { areNavigationScreensEqual, type NavigationScreenState } from '@gitbook/app-navigation';
import { flex, ModalAnimateLifecycle, PortalAbsolute, zIndexes } from '@gitbook/react-uikit';
import { AlertBanner, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { EnableHead } from '@gitbook/util-react-head';
import { useLiveRef } from '@gitbook/util-react-hooks';

import { useUserCustomClaims } from '../state/auth/provider';
import { useNavigationState } from './NavigationProvider';
import {
    NavigationIsCurrentScreenContext,
    NavigationPassiveIsCurrentScreenContext,
} from './useIsCurrentScreen';
import { useNavigation } from './useNavigation';

/**
 * Component to render the navigation stack using a specific renderer.
 */
export function NavigationScreenRenderer(props: {
    /** Component to render one screen */
    Screen: React.ComponentType<{
        screen: NavigationScreenState;
        isCurrent: boolean;
        isOverlay: boolean;
    }>;
    /** Fallback component to render when screen is loading */
    fallback: React.ReactElement;
}): React.ReactElement {
    const { Screen, fallback } = props;
    const { screen, overlay } = useNavigationState();
    const claims = useUserCustomClaims();

    const overlayDisplayType = overlay ? getOverlayDisplayType(overlay) : null;

    return (
        <>
            {claims.impersonator ? (
                <AlertBanner
                    kind="danger"
                    title="You're impersonating a user"
                    details="Be careful navigating the app, close the window once you're done."
                />
            ) : null}
            <EnableHead enabled={!overlay}>
                <ScreenLayer
                    screen={screen}
                    Screen={Screen}
                    isCurrent={!overlay}
                    isOverlay={false}
                />
            </EnableHead>
            <ModalAnimateLifecycle>
                {overlay && overlayDisplayType === 'uikit-dialog' ? (
                    <PortalAbsolute>
                        <View style={[StyleSheet.absoluteFill, styles.isOverlay]}>
                            <View style={[flex.flex1]}>
                                <React.Suspense fallback={fallback}>
                                    <ScreenLayer
                                        screen={overlay}
                                        Screen={Screen}
                                        isCurrent
                                        isOverlay
                                    />
                                </React.Suspense>
                            </View>
                        </View>
                    </PortalAbsolute>
                ) : null}
            </ModalAnimateLifecycle>
            <DialogScreenRenderer fallback={fallback}>
                {overlay && overlayDisplayType === 'spine-dialog' ? (
                    <ScreenLayer screen={overlay} Screen={Screen} isCurrent isOverlay />
                ) : null}
            </DialogScreenRenderer>
        </>
    );
}

/**
 * Function to decide how to display the overlay.
 * - custom: not displayed, handle in the route
 * - spine-dialog: displayed in a Spine dialog
 * - uikit-dialog: displayed in a UIKit dialog
 */
function getOverlayDisplayType(overlay: NavigationScreenState) {
    switch (overlay.type) {
        case 'space':
            return 'custom';
        case 'org_invite':
            return 'spine-dialog';
        default:
            return 'uikit-dialog';
    }
}

function DialogScreenRenderer(props: { children: React.ReactNode; fallback: React.ReactElement }) {
    const { children, fallback } = props;
    const { navigate } = useNavigation();

    const lastChildrenRef = React.useRef<React.ReactNode>(null);
    React.useLayoutEffect(() => {
        if (children) {
            lastChildrenRef.current = children;
        }
    }, [children]);

    const trigger = useDialogTriggerState({
        isOpen: Boolean(props.children),
        onOpenChange: (isOpen) => {
            if (!isOpen) {
                navigate({ overlay: null });
            }
        },
    });

    return (
        <DialogRoot {...trigger.rootProps}>
            <React.Suspense fallback={fallback}>
                {children ?? lastChildrenRef.current}
            </React.Suspense>
        </DialogRoot>
    );
}

const styles = StyleSheet.create({ isOverlay: { zIndex: zIndexes.OVERLAY } });

/**
 * Render a unique screen with the right context.
 * Optimied to skip rendering if not changed so that opening an overlay is optimized.
 */
const ScreenLayer = React.memo(
    function ScreenLayerMemo(props: {
        screen: NavigationScreenState;
        Screen: React.ComponentType<{
            screen: NavigationScreenState;
            isCurrent: boolean;
            isOverlay: boolean;
        }>;
        isOverlay: boolean;
        isCurrent: boolean;
    }): React.ReactElement {
        const { screen, isOverlay, isCurrent, Screen } = props;

        const passiveCurrentScreenRef = useLiveRef(isCurrent);

        return (
            <NavigationPassiveIsCurrentScreenContext.Provider value={passiveCurrentScreenRef}>
                <NavigationIsCurrentScreenContext.Provider value={isCurrent}>
                    <Screen screen={screen} isCurrent={isCurrent} isOverlay={isOverlay} />
                </NavigationIsCurrentScreenContext.Provider>
            </NavigationPassiveIsCurrentScreenContext.Provider>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.isCurrent === nextProps.isCurrent &&
            prevProps.isOverlay === nextProps.isOverlay &&
            prevProps.Screen === nextProps.Screen &&
            areNavigationScreensEqual(prevProps.screen, nextProps.screen)
        );
    }
);
